/**
 * API Client - Base HTTP client for sidecar API
 * Handles authentication, errors, and retries
 */

import { getToken, clearToken, getFetchCredentials } from '../utils/storage.js';
import { getMachineBaseURL, getActiveMachine, getUploadSizeLimit } from '../utils/config.js';
import { isWebKitBrowser } from '../utils/browser.js';

/**
 * Get base URL from active machine config
 * @returns {string} Base URL
 */
function getBaseURL() {
    return getMachineBaseURL();
}

/**
 * Make authenticated API request
 * @param {string} endpoint - API endpoint (e.g., '/api/fs/list')
 * @param {Object} options - Fetch options
 * @returns {Promise<any>} Response data
 */
export async function apiRequest(endpoint, options = {}) {
    const activeMachine = getActiveMachine();
    const token = getToken(activeMachine?.name || 'default');
    const baseURL = getBaseURL();
    
    const headers = {
        'Content-Type': 'application/json',
        ...options.headers,
    };
    
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    
    const url = `${baseURL}${endpoint}`;
    
    try {
        const response = await fetch(url, {
            ...options,
            headers,
        });
        
        // Handle 401 Unauthorized - token expired
        if (response.status === 401) {
            clearToken(activeMachine?.name || 'default');
            throw new Error('Authentication required');
        }
        
        // Read response body as text first, then try JSON parse
        const text = await response.text();
        let data;
        try {
            data = JSON.parse(text);
        } catch {
            // Response is not JSON — use status text or raw body as error message
            if (!response.ok) {
                throw new Error(text || response.statusText || `Request failed (${response.status})`);
            }
            // 2xx but non-JSON (shouldn't happen for this API, but handle gracefully)
            return text;
        }
        
        // Handle API error responses (JSON body with { error: true, message: "..." })
        if (data.error) {
            throw new Error(data.message || 'API request failed');
        }
        
        if (!response.ok) {
            throw new Error(data.message || `Request failed (${response.status})`);
        }
        
        return data;
        
    } catch (error) {
        // Network errors, JSON parse errors, etc.
        console.error(`API request failed: ${endpoint}`, error);
        throw error;
    }
}

/**
 * Read a response body stream chunk-by-chunk and report progress.
 * @param {Response} response - The fetch Response object
 * @param {Function} onProgress - Optional callback function (percent, metrics)
 * @returns {Promise<Blob>} The combined binary Blob
 */
async function readResponseStreamWithProgress(response, onProgress) {
    if (!onProgress) {
        return response.blob();
    }

    const contentLengthHeader = response.headers.get('content-length');
    const totalBytes = contentLengthHeader ? parseInt(contentLengthHeader, 10) : null;
    const lengthComputable = totalBytes !== null && !isNaN(totalBytes) && totalBytes > 0;

    const reader = response.body.getReader();
    const chunks = [];
    let loadedBytes = 0;

    while (true) {
        const { done, value } = await reader.read();
        if (done) {
            break;
        }

        chunks.push(value);
        loadedBytes += value.length;

        const percent = lengthComputable ? (loadedBytes / totalBytes) * 100 : Number.NaN;
        onProgress(percent, {
            loaded: loadedBytes,
            total: totalBytes,
            lengthComputable,
            timestamp: Date.now(),
        });
    }

    // Combine chunks into a single Blob
    return new Blob(chunks, {
        type: response.headers.get('content-type') || 'application/octet-stream'
    });
}

/**
 * Read file as Blob inline
 * @param {string} path - Absolute file path
 * @returns {Promise<Blob>} Response blob
 */
export async function apiReadBlob(path) {
    const activeMachine = getActiveMachine();
    const token = getToken(activeMachine?.name || 'default');
    const baseURL = getBaseURL();
    
    const url = `${baseURL}/api/fs/read?path=${encodeURIComponent(path)}`;
    
    const response = await fetch(url, {
        headers: {
            'Authorization': `Bearer ${token}`,
        },
    });
    
    if (!response.ok) {
        throw new Error(`Read failed: ${response.statusText}`);
    }
    
    return response.blob();
}

/**
 * Download file/folder (returns blob)
 * @param {string} path - File/folder path
 * @param {Function} onProgress - Optional progress callback
 * @param {string} endpoint - API endpoint (default: /api/fs/download)
 * @returns {Promise<Blob>} File blob
 */
export async function apiDownload(path, onProgress = null, endpoint = '/api/fs/download') {
    const activeMachine = getActiveMachine();
    const token = getToken(activeMachine?.name || 'default');
    const baseURL = getBaseURL();
    
    const url = `${baseURL}${endpoint}?path=${encodeURIComponent(path)}`;
    
    const response = await fetch(url, {
        headers: {
            'Authorization': `Bearer ${token}`,
        },
    });
    
    if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`);
    }
    
    return readResponseStreamWithProgress(response, onProgress);
}

/**
 * POST request that returns a binary blob (e.g., batch download as ZIP)
 * @param {string} endpoint - API endpoint
 * @param {any} body - Request body (will be JSON-stringified)
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise<Blob>} Response blob
 */
export async function apiPostForBlob(endpoint, body, onProgress = null) {
    const activeMachine = getActiveMachine();
    const token = getToken(activeMachine?.name || 'default');
    const baseURL = getBaseURL();
    const url = `${baseURL}${endpoint}`;
    
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
    });
    
    if (response.status === 401) {
        clearToken(activeMachine?.name || 'default');
        throw new Error('Authentication required');
    }
    if (!response.ok) {
        throw new Error(`Request failed: ${response.statusText}`);
    }
    return readResponseStreamWithProgress(response, onProgress);
}

/**
 * Upload files (multipart/form-data)
 * @param {string} targetPath - Target directory path
 * @param {FileList|Array} files - Files to upload. Each item can be a File object
 *   or an object { file: File, name: string } for path-preserving uploads.
 * @param {Function} onProgress - Progress callback (percent)
 * @returns {Promise<any>} Upload response
 */
export async function apiUpload(targetPath, files, onProgress) {
    const activeMachine = getActiveMachine();
    const token = getToken(activeMachine?.name || 'default');
    const baseURL = getBaseURL();
    
    const formData = new FormData();
    let totalSize = 0;
    for (const item of files) {
        if (item instanceof File) {
            formData.append('files', item);
            totalSize += item.size;
        } else if (item && item.file) {
            // Path-preserving: use custom name (e.g., 'subdir/file.txt')
            formData.append('files', item.file, item.name);
            totalSize += item.file.size;
        }
    }

    // Pre-check: the sidecar rejects uploads over the configured limit via
    // early 413, but the browser never sees that status (connection resets
    // mid-stream → status 0). Catch it client-side instead.
    const limitMB = getUploadSizeLimit();
    const limitBytes = limitMB * 1024 * 1024;
    if (totalSize > limitBytes) {
        return Promise.reject(new Error(`File too large (limit: ${limitMB >= 1024 ? (limitMB / 1024) + ' GB' : limitMB + ' MB'})`));
    }
    
    const url = `${baseURL}/api/fs/upload?path=${encodeURIComponent(targetPath)}`;
    
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        // Progress tracking
        xhr.upload.addEventListener('progress', (e) => {
            if (onProgress) {
                const percent = e.lengthComputable && e.total > 0
                    ? (e.loaded / e.total) * 100
                    : Number.NaN;
                onProgress(percent, {
                    loaded: e.loaded,
                    total: e.total,
                    lengthComputable: e.lengthComputable,
                    timestamp: Date.now(),
                });
            }
        });
        
        // Use loadend (fires after load, error, or abort) so we always
        // see xhr.status — even when the server sends 413 early and the
        // browser fires 'error' instead of 'load' due to a mid-upload
        // connection reset.
        xhr.addEventListener('loadend', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve(JSON.parse(xhr.responseText));
            } else if (xhr.status === 413) {
                reject(new Error('File too large (server limit: 1 GB)'));
            } else if (xhr.status === 0) {
                reject(new Error('Upload failed: Network error'));
            } else {
                reject(new Error(`Upload failed: ${xhr.statusText}`));
            }
        });
        
        xhr.open('POST', url);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.send(formData);
    });
}

/**
 * Download file/folder from an arbitrary machine URL (cross-machine transfer).
 * Does NOT use getBaseURL() — caller provides the source machine's base URL.
 * @param {string} baseUrl - Source machine base URL (e.g., 'http://host:3000')
 * @param {string} token - Bearer token for the source machine
 * @param {string} path - File/folder path on the source machine
 * @returns {Promise<{blob: Blob, contentType: string}>}
 */
export async function apiDownloadFromUrl(baseUrl, token, path) {
    const url = `${baseUrl}/api/fs/download?path=${encodeURIComponent(path)}`;
    const response = await fetch(url, {
        headers: {
            'Authorization': `Bearer ${token}`,
        },
    });

    if (!response.ok) {
        throw new Error(`Download from ${baseUrl} failed: ${response.statusText}`);
    }

    const blob = await response.blob();
    const contentType = response.headers.get('content-type') || 'application/octet-stream';
    return { blob, contentType };
}

/**
 * List directory contents from an arbitrary machine URL.
 * @param {string} baseUrl - Source machine base URL
 * @param {string} token - Bearer token
 * @param {string} path - Directory path
 * @returns {Promise<Array<{name: string, path: string, is_dir: boolean, size: number, modified: number, perms: string}>>}
 */
export async function apiListFromUrl(baseUrl, token, path) {
    const url = `${baseUrl}/api/fs/list?path=${encodeURIComponent(path)}`;
    const response = await fetch(url, {
        headers: {
            'Authorization': `Bearer ${token}`,
        },
    });

    if (!response.ok) {
        throw new Error(`List from ${baseUrl} failed: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Build a direct browser-usable URL for reading a file via cookie auth.
 * Suitable for <img src>, <video src>, <audio src>, <iframe src>.
 * @param {string} path - File path on the server
 * @param {number} cacheSecs - Optional Cache-Control max-age seconds
 * @returns {string} Full URL
 */
export function getReadURL(path, cacheSecs = 0) {
    const machine = getActiveMachine();
    const baseURL = getBaseURL();
    let url = `${baseURL}/api/fs/read?path=${encodeURIComponent(path)}`;
    if (cacheSecs > 0) {
        url += `&cache=${cacheSecs}`;
    }
    return appendFetchAuthToMediaUrl(url, machine);
}

/**
 * Build a direct browser-usable URL for a file thumbnail via cookie auth.
 * Suitable for <img src> in file browser views.
 * @param {string} path - File path on the server
 * @param {number} cacheSecs - Cache-Control max-age seconds (default: 7 days)
 * @returns {string} Full URL
 */
export function getThumbnailURL(path, cacheSecs = 604800) {
    const machine = getActiveMachine();
    const baseURL = getBaseURL();
    const url = `${baseURL}/api/fs/thumbnail?path=${encodeURIComponent(path)}&cache=${cacheSecs}`;
    return appendFetchAuthToMediaUrl(url, machine);
}

function appendFetchAuthToMediaUrl(url, machine) {
    // Only enable URL auth for remote WebKit clients.
    if (!machine || machine.isSelf || !isWebKitBrowser()) {
        return url;
    }

    const creds = getFetchCredentials(machine.name || 'default');
    if (!creds?.fetchToken) {
        return url;
    }

    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}token=${encodeURIComponent(creds.fetchToken)}`;
}
